/**
 * Browser contract: consume bounded server projections, never infer ownership.
 * This module deliberately has no imports, captured data, or identity indexes.
 */
let serverPolicy;
export const LEGACY_CANONICAL_ORIGIN = "https://driveshare.com";
const cars = new Map();
const routes = new Map();
const references = new Map();
const listeners = new Set();
export function installServerCanonicalPolicy(policy) {
  if (typeof window !== "undefined") throw Error("Server policy cannot be installed in a browser");
  serverPolicy = policy;
}
export function rememberCanonicalPayload(value) {
  // SSR reads its request-owned seed directly; no cross-request identity cache.
  if (serverPolicy) return;
  if (!value || typeof value !== "object") return;
  if (value.publicCanonical && (value.carId ?? value.id) != null) {
    cars.set(String(value.carId ?? value.id), value.publicCanonical);
    const { off, on } = value.publicCanonical;
    if (on?.legacyUrl && off?.url && on?.path) {
      references.set(off.url, on.legacyUrl);
      references.set(new URL(off.url).origin + on.path, on.legacyUrl);
    }
  }
  if (value.publicCanonicalRoute?.pathname) {
    routes.set(value.publicCanonicalRoute.pathname, value.publicCanonicalRoute);
  }
  if (value.canonicalReferences) {
    for (const [from, to] of Object.entries(value.canonicalReferences)) references.set(from, to);
  }
  for (const [key, child] of Object.entries(value)) {
    if (!["publicCanonical", "publicCanonicalRoute", "canonicalReferences"].includes(key)) {
      if (Array.isArray(child)) child.forEach(rememberCanonicalPayload);
      else if (child && typeof child === "object") rememberCanonicalPayload(child);
    }
  }
  for (const listener of listeners) listener();
}
export function clearCanonicalPayloads() {
  cars.clear(); routes.clear(); references.clear();
  for (const listener of listeners) listener();
}
export function subscribeCanonicalPayloads(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function readCanonicalRoute(pathname) {
  return routes.get(pathname);
}
export function readResolvedCarIdentity(car, options = {}) {
  if (serverPolicy) return serverPolicy.identity(car, options);
  // A fresh bounded batch supersedes a stale frozen bootstrap's own projection.
  const projection = (!serverPolicy ? cars.get(String(car.id)) : null) || car.publicCanonical;
  if (projection) {
    const result = options.legacySeoEnabled ? projection.on : projection.off;
    if (typeof result?.path !== "string" || !result.path.startsWith("/") ||
        result.path.startsWith("//") || /[?#\\\u0000-\u001f]/.test(result.path)) {
      return { kind: "unresolved", reason: result?.reason || "server-canonical-unresolved" };
    }
    return { kind: "resolved", path: result.path, canonical: result.url };
  }
  return { kind: "unresolved", reason: "server-canonical-projection-missing" };
}
export function resolvedLegacyCanonical(pathname, fallback, hints = {}) {
  if (serverPolicy) return serverPolicy.legacy(pathname, fallback, hints);
  const route = routes.get(pathname);
  if (!hints.legacySeoEnabled) return route?.off?.url || fallback;
  if (route) return route.on?.url || fallback;
  const car = hints.currentCar;
  const projection = car?.publicCanonical || (car?.id != null ? cars.get(String(car.id)) : null);
  if (projection?.on?.legacyUrl &&
      (pathname === projection.on.path || /^\/cars\/\d+(?:\/|$)/.test(pathname))) {
    return projection.on.legacyUrl;
  }
  return fallback;
}
export function resolvedLegacyRoute(pathname, enabled) {
  if (serverPolicy) return serverPolicy.route(pathname);
  if (!enabled) return routes.get(pathname)?.off || null;
  return routes.get(pathname)?.on || null;
}
export function resolvedCanonicalJsonLd(value, oldUrls, canonical, enabled) {
  if (serverPolicy) return serverPolicy.jsonLd(value, oldUrls, canonical);
  if (!enabled && ![...routes.values()].some(route => route.off?.url === canonical)) return value;
  const owners = new Set(oldUrls.filter(Boolean).map(url => url.split("#")[0]));
  function visit(node) {
    if (Array.isArray(node)) return node.map(visit);
    if (!node || typeof node !== "object") return node;
    return Object.fromEntries(Object.entries(node).map(([key, value]) => {
      if (["@id", "url", "urlTemplate"].includes(key) && typeof value === "string") {
        if (owners.has(value.split("#")[0])) {
          return [key, canonical + (value.includes("#") ? `#${value.split("#").slice(1).join("#")}` : "")];
        }
        return [key, references.get(value) ?? value];
      }
      return [key, visit(value)];
    }));
  }
  return visit(value);
}
